import { useEffect, useMemo, useState } from "react";
import { Search, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { cityApi } from "@/lib/api";
import { toast } from "sonner";

export default function CitiesAdmin() {
  const [cities, setCities] = useState([]);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState({});

  const load = () => cityApi.list().then(setCities);
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return cities;
    return cities.filter((c) => c.name.toLowerCase().includes(s) || c.state.toLowerCase().includes(s));
  }, [cities, q]);

  const toggle = async (city, enabled) => {
    setSaving((p) => ({ ...p, [city.id]: true }));
    try {
      const updated = await cityApi.update(city.id, { enabled });
      setCities((prev) => prev.map((c) => (c.id === city.id ? updated : c)));
      toast.success(`${city.name} ${enabled ? "enabled" : "disabled"}`);
    } catch (e) {
      toast.error("Failed to update city");
    } finally {
      setSaving((p) => ({ ...p, [city.id]: false }));
    }
  };

  const enabledCount = cities.filter((c) => c.enabled).length;

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-display text-4xl font-black">Cities</h1>
          <p className="text-gray-500 mt-1">Toggle which cities are available to users on the public site.</p>
        </div>
        <div className="text-right">
          <div className="text-xs font-bold uppercase tracking-widest text-gray-500">Enabled</div>
          <div className="font-display text-3xl font-black text-[#FA003F]">{enabledCount}<span className="text-gray-300 text-xl"> / {cities.length}</span></div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-black/10 overflow-hidden">
        <div className="p-4 border-b border-black/5">
          <div className="relative max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <Input
              data-testid="cities-search-input"
              placeholder="Search cities or states…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-11 h-11 rounded-full"
            />
          </div>
        </div>

        <div className="max-h-[70vh] overflow-y-auto divide-y divide-black/5">
          {filtered.map((c) => (
            <div
              key={c.id}
              data-testid={`admin-city-row-${c.key}`}
              className="px-6 py-4 flex items-center justify-between hover:bg-[#F9F8F6]"
            >
              <div className="flex items-center gap-3">
                <div className={`h-8 w-8 rounded-lg grid place-items-center ${c.enabled ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-400"}`}>
                  {c.enabled ? <Check className="h-4 w-4" /> : <span className="text-xs">—</span>}
                </div>
                <div>
                  <div className="font-bold">{c.name}</div>
                  <div className="text-xs text-gray-500">{c.state}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-xs font-bold px-2 py-1 rounded-full ${c.enabled ? "bg-emerald-100 text-emerald-800" : "bg-gray-100 text-gray-500"}`}>
                  {c.enabled ? "Active" : "Inactive"}
                </span>
                <Switch
                  data-testid={`city-toggle-${c.key}`}
                  checked={c.enabled}
                  disabled={saving[c.id]}
                  onCheckedChange={(v) => toggle(c, v)}
                  className="data-[state=checked]:bg-[#FA003F]"
                />
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="px-6 py-16 text-center text-gray-500">No cities match your search.</div>
          )}
        </div>
      </div>
    </div>
  );
}
